/* Pocket reviews page (sections/pocket-reviews.liquid).
   Loads every review from Judge.me's public all-reviews feed and builds the page in the browser. */
(() => {
  const FEED = 'https://api.judge.me/reviews/all_reviews_js_based';
  const CACHE_KEY = 'pocketReviews:v3';
  const CACHE_MS = 30 * 60 * 1000;

  const TOPICS = [
    { k: 'glow', label: '💡 The glow', re: /\b(glow\w*|warm\w*|light(s|ing)?|ambien\w*|cos[yi]\w*|soft)\b/i },
    { k: 'design', label: '🎨 The design', re: /\b(design\w*|unique|art|funky|retro|style|beautiful|gorgeous|stunning)\b/i },
    { k: 'quality', label: '🛠 Quality', re: /\b(quality|well made|sturdy|crafted|solid)\b/i },
    { k: 'gift', label: '🎁 Gifting', re: /\b(gift\w*|present|birthday|christmas|house ?warming)\b/i },
    { k: 'delivery', label: '📦 Delivery', re: /\b(pack\w*|deliver\w*|arriv\w*|shipping|postage|fast|quick\w*|swift)\b/i },
    { k: 'local', label: '🇦🇺 Made in Melbourne', re: /\b(australian|melbourne|local\w*|small business)\b/i },
    { k: 'compliments', label: '💬 Compliments', re: /\b(compliment\w*|friends|guests|everyone)\b/i },
  ];

  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const shortName = (a) => { const p = (a || 'Pocket customer').trim().split(/\s+/); return p.length > 1 ? `${p[0]} ${p[p.length - 1][0]}.` : p[0]; };
  const stars = (n) => `<span class="pr-stars" aria-label="${(+n).toFixed(1)} out of 5 stars">${[1, 2, 3, 4, 5].map((i) => `<i class="${i <= Math.round(n) ? '' : 'is-off'}" aria-hidden="true">★</i>`).join('')}</span>`;
  const pic = (u, w) => u.replace(/width=\d+/, 'width=' + w);
  const sized = (u, w) => (!u ? '' : /width=\d+/.test(u) ? u.replace(/width=\d+/, 'width=' + w) : u + (u.includes('?') ? '&' : '?') + 'width=' + w);
  const date = (d) => (d ? new Date(d).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' }) : '');

  async function loadReviews(shop) {
    try {
      const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
      if (cached && Date.now() - cached.at < CACHE_MS) return cached.reviews;
    } catch (e) { /* storage blocked: just fetch */ }
    /* Judge.me returns up to 25 reviews a page: read the first page, then fetch the rest together. */
    const getPage = async (page) => {
      const res = await fetch(`${FEED}?shop_domain=${encodeURIComponent(shop)}&platform=shopify&per_page=25&page=${page}&review_type=all-reviews`);
      if (!res.ok) throw new Error('Judge.me ' + res.status);
      return res.json();
    };
    const first = await getPage(1);
    const total = (+first.number_of_shop_reviews || 0) + (+first.number_of_product_reviews || 0);
    const pages = Math.min(40, Math.max(1, Math.ceil(total / 25)));
    const rest = await Promise.all(Array.from({ length: pages - 1 }, (_, i) => getPage(i + 2).catch(() => ({ html: '' }))));
    const reviews = [];
    [first, ...rest].forEach((data) => {
      const doc = new DOMParser().parseFromString(data.html || '', 'text/html');
      doc.querySelectorAll('.jdgm-rev').forEach((el) => {
        const text = (sel) => el.querySelector(sel)?.textContent.trim() || '';
        const link = el.querySelector('.jdgm-rev__prod-link');
        reviews.push({
          verified: el.dataset.verifiedBuyer === 'true',
          rating: +(el.querySelector('.jdgm-rev__rating')?.dataset.score || 5),
          date: (el.querySelector('time')?.getAttribute('datetime') || '').slice(0, 10),
          author: text('.jdgm-rev__author'),
          title: text('.jdgm-rev__title'),
          body: (el.querySelector('.jdgm-rev__body')?.innerText || text('.jdgm-rev__body')).trim(),
          handle: (link?.getAttribute('href') || '').split('/products/')[1]?.split(/[?#]/)[0] || '',
          ptitle: link?.textContent.trim() || '',
          pics: [...el.querySelectorAll('.jdgm-rev__pic-link')].map((a) => a.getAttribute('href')),
          reply: text('.jdgm-rev__reply-content'),
        });
      });
    });
    try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), reviews })); } catch (e) { /* ignore */ }
    return reviews;
  }

  function init(root) {
    if (root.dataset.prReady) return;
    root.dataset.prReady = '1';
    const $ = (s) => root.querySelector(s);
    const params = new URLSearchParams(location.search);
    const products = JSON.parse($('[data-pr-products]').textContent || '[]');
    const stats = [...root.querySelectorAll('[data-pr-count]')].map((el) => ({ el, s: parseStat(el.textContent) })).filter((x) => x.s);
    stats.forEach(({ el, s }) => { el.textContent = s.prefix + (0).toFixed(s.decimals) + s.suffix; });

    loadReviews(root.dataset.shop).then(async (all) => { await addMissingProducts(all); build(all); }).catch(() => {
      $('[data-pr-feed]').innerHTML = '<p class="pr-empty">Reviews couldn\'t load just now. Every lamp page has its reviews too.</p>';
      build([]);
    });

    /* Reviews can point at products that aren't in the list (another collection, or an old handle that now
       redirects). Look those up from Shopify's product endpoint so they still get a lamp and a link. */
    async function addMissingProducts(all) {
      const known = new Set(products.map((p) => p.h));
      const missing = [...new Set(all.map((r) => r.handle).filter((h) => h && !known.has(h)))].slice(0, 20);
      const money = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });
      await Promise.all(missing.map(async (h) => {
        try {
          const res = await fetch(`${window.Shopify?.routes?.root || '/'}products/${h}.js`);
          if (!res.ok) return;
          const d = await res.json();
          const img = d.featured_image ? (d.featured_image.startsWith('//') ? 'https:' + d.featured_image : d.featured_image) : '';
          products.push({ h, t: d.title, u: `/products/${d.handle}`, p: money.format(d.price / 100), c: d.price, v: d.vendor, a: d.available, i: img, r: null, n: 0 });
        } catch (e) { /* skip */ }
      }));
    }

    function build(all) {
      const reviews = all.filter((r) => r.body);
      reviewsIndex = reviews;
      /* Group colours (separate products, same title) into one lamp; link to the colour with most reviews. */
      const byHandle = {}; products.forEach((p) => (byHandle[p.h] = p));
      const byTitle = {}; products.forEach((p) => { if (!byTitle[p.t.toLowerCase()] || p.n > byTitle[p.t.toLowerCase()].n) byTitle[p.t.toLowerCase()] = p; });
      const productOf = (r) => byHandle[r.handle] || byTitle[(r.ptitle || '').toLowerCase()] || null;
      const lampsByTitle = {};
      products.forEach((p) => {
        const l = (lampsByTitle[p.t] ||= { title: p.t, colours: [], reviews: [] });
        l.colours.push(p);
      });
      reviews.forEach((r) => { const p = productOf(r); if (p) lampsByTitle[p.t].reviews.push(r); });
      const lamps = Object.values(lampsByTitle).map((l) => {
        const main = [...l.colours].sort((a, b) => b.n - a.n)[0];
        const n = l.reviews.length || l.colours.reduce((a, p) => a + p.n, 0);
        const avg = l.reviews.length ? l.reviews.reduce((a, r) => a + r.rating, 0) / l.reviews.length : l.colours.reduce((a, p) => a + (+p.r || 0) * p.n, 0) / Math.max(1, l.colours.reduce((a, p) => a + p.n, 0));
        return { ...l, main, n, avg };
      }).filter((l) => l.reviews.length > 0 || (reviews.length === 0 && l.n > 0)).sort((a, b) => b.n - a.n || b.avg - a.avg);
      const lampOf = (r) => { const p = productOf(r); return p ? lamps.find((l) => l.title === p.t) || null : null; };

      /* The average comes from the live reviews when they load; the other stats come from theme settings. */
      if (reviews.length) {
        const a = stats.find((x) => x.el.matches('[data-pr-stat="avg"]'));
        if (a) a.s.value = Math.round((reviews.reduce((s, r) => s + r.rating, 0) / reviews.length) * 10) / 10;
      }
      stats.forEach(({ el, s }) => countUp(el, s.value, s.decimals, s.suffix, s.prefix));

      /* Swipe cards */
      const skip = +root.dataset.stackSkip || 0;
      const featured = reviews.filter((r) => r.rating === 5 && r.body.length > 60 && r.body.length < 200).slice(skip, skip + 9);
      /* Keep a multiple of 3 so the three card colours never repeat side by side when the stack loops. */
      if (featured.length > 3) featured.length -= featured.length % 3;
      if (featured.length) initStack($('[data-pr-stack]'), featured, $('[data-pr-dots]'), lampOf), ($('[data-pr-hint]').hidden = false);
      else $('[data-pr-stack]').remove();

      /* Most-loved lamps */
      $('[data-pr-lamps]').innerHTML = lamps.map((l, i) => {
        const q = l.reviews.find((r) => r.rating === 5 && r.body.length > 30 && r.body.length < 140) || l.reviews[0];
        return `<button class="pr-lamp" role="listitem" data-pr-open="${esc(l.title)}">
          <div class="pr-lamp__media"><img loading="lazy" src="${esc(sized(l.main.i, 500))}" alt="${esc(l.title)}" width="500" height="500">
            ${i < 3 ? `<span class="badge color-accent-1">#${i + 1} most loved</span>` : ''}</div>
          <h3 class="card__heading">${esc(l.title)}</h3>
          <div class="pr-lamp__meta"><span class="badge card-rating-badge color-background-2"><span aria-hidden="true">★</span> ${l.avg.toFixed(1)}</span><span class="caption-with-letter-spacing light">${l.n} review${l.n > 1 ? 's' : ''}</span></div>
          ${q ? `<p class="pr-lamp__quote">“${esc(q.body)}”</p>` : ''}
          <span class="pr-lamp__price">${esc(l.main.p)}</span>
          <span class="pr-lamp__more">Read reviews</span></button>`;
      }).join('');

      /* Topics */
      const topics = [{ k: 'all', label: '★ All', rs: reviews }, { k: 'photos', label: '📸 Photos', rs: reviews.filter((r) => r.pics.length) },
        ...TOPICS.map((t) => ({ ...t, rs: reviews.filter((r) => t.re.test(r.title + ' ' + r.body)) })).filter((t) => t.rs.length >= 3)];
      let topic = topics.find((t) => t.k === params.get('topic')) || topics[0];
      let shown = 8;
      const chips = $('[data-pr-chips]');
      chips.innerHTML = topics.map((t) => `<button class="pr-chip" data-k="${t.k}" aria-pressed="${t === topic}">${t.label}<span>${t.rs.length}</span></button>`).join('');
      const feed = $('[data-pr-feed]');
      const renderFeed = () => {
        if (!reviews.length) return;
        let list = topic.rs;
        if (topic.k === 'all') list = [...list].sort((a, b) => (b.pics.length > 0) - (a.pics.length > 0) || b.rating - a.rating);
        feed.innerHTML = list.slice(0, shown).map((r, i) => quote(r, TOPICS.find((t) => t.k === topic.k) || null, i, lampOf(r))).join('') +
          (list.length > shown ? `<button class="button button--secondary pr-more">Show more (${list.length - shown})</button>` : '');
      };
      chips.addEventListener('click', (e) => {
        const b = e.target.closest('.pr-chip'); if (!b) return;
        topic = topics.find((t) => t.k === b.dataset.k); shown = 8;
        chips.querySelectorAll('.pr-chip').forEach((c) => c.setAttribute('aria-pressed', c === b));
        b.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
        renderFeed();
      });
      feed.addEventListener('click', (e) => {
        if (e.target.closest('.pr-more')) { shown += 8; renderFeed(); return; }
        const more = e.target.closest('.pr-q__more');
        if (more) { const t = more.previousElementSibling; t.style.webkitLineClamp = 'unset'; t.style.display = 'block'; more.remove(); }
      });
      renderFeed();

      /* Customer photos */
      const withPics = reviews.filter((r) => r.pics.length);
      if (withPics.length) {
        $('[data-pr-photos-wrap]').hidden = false;
        $('[data-pr-photos]').innerHTML = withPics.map((r, i) => `<button class="pr-photo" data-pr-photo="${i}"><img loading="lazy" src="${esc(pic(r.pics[0], 400))}" alt="Customer photo of the ${esc(lampOf(r)?.title || 'Pocket lamp')}"><small>${esc(shortName(r.author))} · ${esc(lampOf(r)?.title || 'Pocket')}</small></button>`).join('');
      }

      /* Bottom sheet */
      const sheet = $('[data-pr-sheet]'), sheetBg = $('[data-pr-sheet-bg]');
      /* Move to <body> so the sheet sits above the theme's sticky header. */
      document.body.append(sheetBg, sheet);
      let lastFocus = null;
      const openSheet = (html) => {
        lastFocus = document.activeElement;
        sheet.innerHTML = `<div class="pr-sheet__grab"></div><button class="pr-sheet__close" aria-label="Close">×</button>${html}`;
        sheet.classList.add('is-open'); sheetBg.classList.add('is-open'); sheet.setAttribute('aria-hidden', 'false');
        document.body.classList.add('pr-lock');
        requestAnimationFrame(() => requestAnimationFrame(() => sheet.querySelectorAll('.pr-bar i').forEach((i) => (i.style.width = i.dataset.w + '%'))));
        sheet.querySelector('.pr-sheet__close').focus({ preventScroll: true });
      };
      const closeSheet = () => {
        sheet.classList.remove('is-open'); sheetBg.classList.remove('is-open'); sheet.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('pr-lock'); lastFocus?.focus?.({ preventScroll: true });
      };
      const openLamp = (l) => {
        const h = [5, 4, 3, 2, 1].map((s) => ({ s, n: l.reviews.filter((r) => r.rating === s).length }));
        const photos = l.reviews.flatMap((r) => r.pics).slice(0, 10);
        openSheet(`<div class="pr-sheet__body">
          <div class="pr-sheet__head"><img src="${esc(sized(l.main.i, 200))}" alt=""><div><h3>${esc(l.title)}</h3>${stars(l.avg)} <small>${l.avg.toFixed(1)} · ${l.n} review${l.n > 1 ? 's' : ''}</small></div></div>
          ${l.reviews.length ? `<div class="pr-histo">${h.map((r) => `<div><span>${r.s}★</span><span class="pr-bar"><i data-w="${(r.n / l.reviews.length) * 100}"></i></span><span>${r.n}</span></div>`).join('')}</div>` : ''}
          ${photos.length ? `<div class="pr-scroller pr-sheet__photos">${photos.map((u) => `<img loading="lazy" src="${esc(pic(u, 260))}" alt="Customer photo">`).join('')}</div>` : ''}
          <div class="pr-sheet__list">${l.reviews.map((r, i) => quote({ ...r, pics: [] }, null, i, null)).join('')}</div></div>
          <div class="pr-sheet__foot"><a class="button button--primary" href="${esc(l.main.u)}">View the ${esc(l.title)} · ${esc(l.main.p)}</a></div>`);
      };
      const openPhoto = (r) => {
        const l = lampOf(r);
        openSheet(`<div class="pr-sheet__body"><div class="pr-sheet__photo-view"><img src="${esc(pic(r.pics[0], 1024))}" alt="Customer photo"></div>
          <div style="margin-top:1.2rem">${quote({ ...r, pics: [] }, null, 0, null)}</div></div>
          ${l ? `<div class="pr-sheet__foot"><a class="button button--primary" href="${esc(l.main.u)}">View the ${esc(l.title)} · ${esc(l.main.p)}</a></div>` : ''}`);
      };
      sheetBg.addEventListener('click', closeSheet);
      sheet.addEventListener('click', (e) => {
        if (e.target.closest('.pr-sheet__close')) { closeSheet(); return; }
        const more = e.target.closest('.pr-q__more');
        if (more) { const t = more.previousElementSibling; t.style.webkitLineClamp = 'unset'; t.style.display = 'block'; more.remove(); }
      });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && sheet.classList.contains('is-open')) closeSheet(); });
      let sy = 0;
      sheet.addEventListener('touchstart', (e) => { sy = e.touches[0].clientY; }, { passive: true });
      sheet.addEventListener('touchend', (e) => { const b = sheet.querySelector('.pr-sheet__body'); if (e.changedTouches[0].clientY - sy > 90 && (!b || b.scrollTop <= 0)) closeSheet(); }, { passive: true });
      root.addEventListener('click', (e) => {
        const o = e.target.closest('[data-pr-open]');
        if (o) { e.preventDefault(); const l = lamps.find((x) => x.title === o.dataset.prOpen); if (l) openLamp(l); return; }
        const ph = e.target.closest('[data-pr-photo]');
        if (ph) { openPhoto(withPics[+ph.dataset.prPhoto]); return; }
        const fp = e.target.closest('[data-pr-feed-photo]');
        if (fp) { const r = reviews[+fp.dataset.prFeedPhoto]; if (r) openPhoto(r); }
      });
      /* Links from emails */
      const deep = params.get('product');
      if (deep) {
        const p = byHandle[deep] || products.find((x) => x.h === deep);
        const l = p && lamps.find((x) => x.title === p.t);
        if (l) setTimeout(() => openLamp(l), 350);
      } else if (params.get('topic')) {
        setTimeout(() => $('[data-pr-topics-wrap]').scrollIntoView({ behavior: 'smooth' }), 300);
      }

      initDock(root);
    }

    let reviewsIndex = [];
    function quote(r, t, i, lamp) {
      const idx = reviewsIndex.indexOf(r);
      const body = t ? esc(r.body).replace(new RegExp(t.re.source, 'gi'), (m) => `<mark>${m}</mark>`) : esc(r.body);
      const who = `<div class="pr-q__who"><span>${esc(shortName(r.author))}${r.verified ? ' ✓' : ''}</span>${lamp ? `<a href="${esc(lamp.main.u)}">${esc(lamp.title)} →</a>` : `<span>${date(r.date)}</span>`}</div>`;
      if (r.pics.length) {
        return `<div class="pr-q pr-q--photo" style="animation-delay:${Math.min(i, 6) * 40}ms">
          <button class="pr-q__img" data-pr-feed-photo="${idx}" aria-label="See the photo and full review"><img loading="lazy" src="${esc(pic(r.pics[0], 500))}" alt="Customer photo"></button>
          ${stars(r.rating)}<div class="pr-q__text">${body}</div>${who}</div>`;
      }
      const long = r.body.length > 280;
      return `<div class="pr-q" style="animation-delay:${Math.min(i, 6) * 40}ms">${stars(r.rating)}${r.title ? `<h4>${esc(r.title)}</h4>` : ''}
        <div class="pr-q__text"${long ? ' style="display:-webkit-box;-webkit-line-clamp:6;-webkit-box-orient:vertical;overflow:hidden"' : ''}>${body}</div>
        ${long ? '<button class="pr-q__more">Read more</button>' : ''}
        ${r.reply ? `<p style="margin:1rem 0 0;font-size:1.25rem;opacity:.85"><b>Reply from the studio:</b> ${esc(r.reply.length > 160 ? r.reply.slice(0, 160) + '…' : r.reply)}</p>` : ''}
        ${who}</div>`;
    }
  }

  function initStack(el, revs, dotsEl, lampOf) {
    const TONES = ['cream', 'white', 'amber'];
    el.innerHTML = revs.map((r, i) => {
      const l = lampOf(r);
      return `<article class="pr-card" data-tone="${TONES[i % TONES.length]}">${stars(r.rating)}${r.title ? `<h3>${esc(r.title)}</h3><p>${esc(r.body)}</p>` : `<p class="pr-card__big">“${esc(r.body)}”</p>`}
        <div class="pr-card__who"><span>${esc(shortName(r.author))}${r.verified ? ' ✓' : ''}</span>${l ? `<a href="${esc(l.main.u)}">${esc(l.title)} →</a>` : ''}</div></article>`;
    }).join('');
    dotsEl.innerHTML = revs.map(() => '<i></i>').join('');
    const cards = [...el.children], n = cards.length;
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    let top = 0, busy = false, lastTouch = 0, visible = true;
    const rel = (i) => (i - top + n) % n;
    const place = (c) => {
      const r = rel(cards.indexOf(c));
      c.style.transform = '';
      c.dataset.pos = r < 3 ? r : 'hide';
      c.setAttribute('aria-hidden', r === 0 ? 'false' : 'true');
    };
    const dots = () => [...dotsEl.children].forEach((d, i) => d.classList.toggle('is-on', i === top));
    cards.forEach(place); dots();

    /* Throw the top card off, and let the rest rise into place while it flies. */
    const next = (dir) => {
      if (busy || n < 2) return;
      busy = true;
      const c = cards[top];
      c.classList.remove('is-dragging');
      c.classList.add('is-flying');
      c.style.transform = `translate(${dir * 140}%, -2rem) rotate(${dir * 22}deg)`;
      top = (top + 1) % n;
      cards.forEach((x) => { if (x !== c) { x.classList.remove('is-dragging'); place(x); } });
      dots();
      setTimeout(() => {
        /* Tuck the thrown card in at the back without animating it there. */
        c.classList.add('no-anim');
        c.classList.remove('is-flying');
        place(c);
        void c.offsetWidth;
        setTimeout(() => { c.classList.remove('no-anim'); busy = false; }, 30);
      }, 480);
    };

    /* Drag: the card follows the finger and the one underneath grows into place. */
    let sx = 0, sy = 0, dx = 0, dy = 0, dragging = false, moved = false, samples = [];
    el.addEventListener('pointerdown', (e) => {
      if (busy || e.target.closest('a') || e.button > 0) return;
      dragging = true; moved = false; sx = e.clientX; sy = e.clientY; dx = dy = 0; samples = [[e.clientX, e.timeStamp]];
      cards[top].classList.add('is-dragging');
      cards[(top + 1) % n].classList.add('is-dragging');
      el.setPointerCapture(e.pointerId);
      lastTouch = Date.now();
    });
    el.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      dx = e.clientX - sx; dy = e.clientY - sy;
      if (Math.abs(dx) > 4) moved = true;
      samples.push([e.clientX, e.timeStamp]); if (samples.length > 5) samples.shift();
      cards[top].style.transform = `translate(${dx}px, ${dy * 0.15}px) rotate(${dx / 12}deg)`;
      const p = Math.min(1, Math.abs(dx) / 140);
      cards[(top + 1) % n].style.transform = `translateY(${1.4 * (1 - p)}rem) scale(${0.95 + 0.05 * p}) rotate(${2.5 * (1 - p) - 1.5 * p}deg)`;
    });
    const end = () => {
      if (!dragging) return;
      dragging = false; lastTouch = Date.now();
      const [x0, t0] = samples[0], [x1, t1] = samples[samples.length - 1];
      const speed = t1 > t0 ? (x1 - x0) / (t1 - t0) : 0;
      if (Math.abs(dx) > 80 || Math.abs(speed) > 0.5) return next(Math.sign(dx || speed));
      if (!moved) return next(-1); /* a tap moves on too */
      cards.forEach((c) => c.classList.remove('is-dragging'));
      place(cards[top]); place(cards[(top + 1) % n]); /* spring back */
    };
    el.addEventListener('pointerup', end);
    el.addEventListener('pointercancel', end);

    /* Moves on by itself, but not while someone is reading, swiping or scrolled away. */
    new IntersectionObserver(([e]) => { visible = e.isIntersecting; }).observe(el);
    if (!reduced) setInterval(() => { if (visible && !document.hidden && !dragging && Date.now() - lastTouch > 7000) next(-1); }, 4800);
  }

  /* This theme scrolls <body> rather than the window, so listen for scrolling anywhere. */
  const onScroll = (fn) => document.addEventListener('scroll', fn, { capture: true, passive: true });

  function countUp(el, target, decimals, suffix = '', prefix = '') {
    if (!el) return;
    const fmt = new Intl.NumberFormat('en-AU', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    const t0 = performance.now();
    const step = (now) => {
      const k = Math.min(1, (now - t0) / 1400), v = target * (1 - Math.pow(1 - k, 3));
      el.textContent = prefix + fmt.format(v) + suffix;
      if (k < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
    /* If the browser isn't animating (background tab, battery saver), still land on the real number. */
    setTimeout(() => { el.textContent = prefix + fmt.format(target) + suffix; }, 1600);
  }

  /* Read a stat like "1,600+", "100%" or "4.9★" into its number and the text around it. */
  function parseStat(text) {
    const m = String(text).trim().match(/^(\D*?)(\d[\d,]*(?:\.\d+)?)(.*)$/);
    if (!m) return null;
    const num = m[2].replace(/,/g, '');
    return { prefix: m[1], value: parseFloat(num), decimals: (num.split('.')[1] || '').length, suffix: m[3] };
  }

  function initDock(root) {
    const dock = root.querySelector('[data-pr-dock]'), hero = root.querySelector('[data-pr-hero]'), cta = root.querySelector('.pr-cta');
    if (!dock) return;
    const check = () => dock.classList.toggle('is-shown', hero.getBoundingClientRect().bottom < 0 && cta.getBoundingClientRect().top > innerHeight);
    onScroll(check); check();
  }

  const start = () => document.querySelectorAll('[data-pocket-reviews]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
  document.addEventListener('shopify:section:load', start);
})();
